import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { capitalizeFirstLetter } from "../../../utils/letters";
import { formatDate } from "../../../utils/date";
import ConfirmModal from "../../../backoffice/components/ConfirmModal";
import { uploadStudents, downloadTemplateStudents, getCompanyInfo, listMyBatches, deleteBatch, listBatchRows } from "../../../api/company";

function formatBatchDate(iso) {
	return formatDate(iso) || iso;
}

function rowOutcomeTableClass(outcome) {
	switch (outcome) {
		case "created":
			return "portal-emp-batch-row portal-emp-batch-row--created";
		case "updated":
			return "portal-emp-batch-row portal-emp-batch-row--updated";
		case "reassigned":
			return "portal-emp-batch-row portal-emp-batch-row--reassigned";
		case "skipped":
			return "portal-emp-batch-row portal-emp-batch-row--skipped";
		default:
			return "";
	}
}

function rowOutcomeLabel(outcome) {
	const m = {
		created: "Nuevo",
		updated: "Actualizado",
		reassigned: "Reasignado",
		skipped: "Omitido",
	};
	return m[outcome] || outcome;
}

/** Celdas vacías / NaN de Excel (legacy) → no mostrar "nan" */
function displayExcelCell(v) {
	if (v == null) return "—";
	const s = String(v).trim();
	if (s === "" || s.toLowerCase() === "nan") return "—";
	return s;
}

function getApiErrorMessage(e, defaultMsg) {
	const status = e?.response?.status;
	if (status === 403) {
		return "No tiene permiso para esta acción. Cierre sesión e inicie nuevamente como representante de la organización desde el enlace de acceso organización.";
	}
	if (status === 401) {
		return "Sesión expirada. Inicia sesión de nuevo.";
	}
	const msg =
		e?.response?.data?.error ||
		e?.response?.data?.detail ||
		(e?.message && typeof e.message === "string" ? e.message : null);
	return msg || defaultMsg;
}

export default function UploadStudentsPage() {
	const [file, setFile] = useState(null);
	const [excelCode, setExcelCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [templateDownloaded, setTemplateDownloaded] = useState(false);
	const [error, setError] = useState(null);
	const [result, setResult] = useState(null);
	const [fieldErrors, setFieldErrors] = useState({});
	const [companyInfo, setCompanyInfo] = useState(null);
	const [infoError, setInfoError] = useState(null);
	const [loadingInfo, setLoadingInfo] = useState(true);
	const [batches, setBatches] = useState([]);
	const [loadingBatches, setLoadingBatches] = useState(true);
	const [batchesError, setBatchesError] = useState(null);
	const [deletingId, setDeletingId] = useState(null);
	const [batchToDelete, setBatchToDelete] = useState(null);

	const [previewBatchId, setPreviewBatchId] = useState(null);
	const [previewFileName, setPreviewFileName] = useState("");
	const [previewRows, setPreviewRows] = useState([]);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState(null);
	const [successMsg, setSuccessMsg] = useState(null);

	const closeBatchPreview = useCallback(() => {
		setPreviewBatchId(null);
		setPreviewFileName("");
		setPreviewRows([]);
		setPreviewError(null);
		setPreviewLoading(false);
	}, []);

	const openBatchPreview = useCallback(async (id, fileName) => {
		const numericId = Number(id);
		setPreviewFileName(fileName || "");
		setPreviewRows([]);
		setPreviewError(null);
		if (!Number.isFinite(numericId) || numericId <= 0) {
			setPreviewBatchId(-1);
			setPreviewError("ID de lote inválido.");
			return;
		}
		setPreviewBatchId(numericId);
		setPreviewLoading(true);
		try {
			const data = await listBatchRows(numericId);
			setPreviewRows(Array.isArray(data) ? data : []);
		} catch (e) {
			const status = e?.response?.status;
			const body = e?.response?.data;
			const apiErr =
				body &&
					typeof body === "object" &&
					typeof body.error === "string"
					? body.error
					: null;
			if (status === 404) {
				setPreviewError(
					apiErr ||
					"No se encontró el lote o el detalle. Si el servidor no está actualizado, reinicia el backend y ejecuta migraciones (companies). Si el lote es muy antiguo, puede no tener filas guardadas.",
				);
			} else {
				setPreviewError(
					apiErr ||
					getApiErrorMessage(
						e,
						"No se pudo cargar el detalle del lote.",
					),
				);
			}
			setPreviewRows([]);
		} finally {
			setPreviewLoading(false);
		}
	}, []);

	useEffect(() => {
		if (previewBatchId == null) return undefined;
		const onKey = (e) => {
			if (e.key === "Escape") closeBatchPreview();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [previewBatchId, closeBatchPreview]);

	useEffect(() => {
		let cancelled = false;
		setLoadingBatches(true);
		setBatchesError(null);
		(async () => {
			try {
				const data = await listMyBatches();
				if (!cancelled) setBatches(Array.isArray(data) ? data : []);
			} catch (e) {
				if (!cancelled) {
					setBatches([]);
					setBatchesError(getApiErrorMessage(e, "No se pudieron cargar los lotes."));
				}
			} finally {
				if (!cancelled) setLoadingBatches(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		let cancelled = false;
		setLoadingInfo(true);
		setInfoError(null);
		(async () => {
			try {
				const info = await getCompanyInfo();
				if (!cancelled) {
					setCompanyInfo(info);
					setExcelCode((prev) => prev || info?.excel_code || "");
				}
			} catch (e) {
				if (!cancelled) {
					setCompanyInfo(null);
					setInfoError(getApiErrorMessage(e, "No se pudo cargar la información de la organización."));
				}
			} finally {
				if (!cancelled) setLoadingInfo(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	const refreshBatches = async () => {
		setBatchesError(null);
		try {
			const data = await listMyBatches();
			setBatches(Array.isArray(data) ? data : []);
		} catch (e) {
			setBatchesError(getApiErrorMessage(e, "No se pudieron actualizar los lotes."));
		}
	};

	const confirmDeleteBatch = async () => {
		if (!batchToDelete) return;
		setDeletingId(batchToDelete.id);
		setError(null);
		setSuccessMsg(null);
		try {
			await deleteBatch(batchToDelete.id);
			await refreshBatches();
			setSuccessMsg(`El lote "${batchToDelete.file_name}" ha sido eliminado correctamente.`);
			setBatchToDelete(null);
		} catch (e) {
			setError(getApiErrorMessage(e, "No se pudo eliminar el lote."));
			setBatchToDelete(null);
		} finally {
			setDeletingId(null);
		}
	};

	const handleDownloadTemplate = async () => {
		setError(null);
		setDownloading(true);
		try {
			await downloadTemplateStudents();
			setTemplateDownloaded(true);
		} catch (e) {
			setError(getApiErrorMessage(e, "No se pudo descargar la plantilla."));
		} finally {
			setDownloading(false);
		}
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		setError(null);
		setResult(null);
		const codeTrimmed = (excelCode || "").trim();
		const err = {};
		if (!file) err.file = "Seleccione un archivo Excel antes de cargar.";
		if (!codeTrimmed) err.excelCode = "Debe ingresar el Código para Excel de su organización (el mismo que aparece en backoffice para esta organización).";
		else if (companyInfo?.excel_code && codeTrimmed !== companyInfo.excel_code) {
			err.excelCode = "El código ingresado no coincide con el de su organización. Utilice el Código para Excel asignado en backoffice.";
		}
		setFieldErrors(err);
		if (Object.keys(err).length > 0) return;

		setLoading(true);
		try {
			const data = await uploadStudents(file, codeTrimmed);
			setResult(data);
			setFile(null);
			setExcelCode(companyInfo?.excel_code ?? codeTrimmed);
			setFieldErrors({});
			const fileInput = document.getElementById("file-cargas");
			if (fileInput) fileInput.value = "";
			await refreshBatches();
		} catch (e2) {
			setError(getApiErrorMessage(e2, "No se pudo procesar el archivo. Revisa que tenga las columnas: Rut, Nombres, Apellidos, Correo."));
		} finally {
			setLoading(false);
		}
	};

	const canSubmit = Boolean(templateDownloaded && file && (excelCode || "").trim() && !loading);

	return (
		<>
			<div className="p-4 backoffice-tables flex-grow-1 overflow-auto font-n">
				<div>
					<div className="container-upload">
						<div>
							<h2 className="portal-emp-title">Carga de estudiantes</h2>
							<p className="portal-emp-intro">
								Cargue un archivo Excel con los datos de sus estudiantes. Utilice la plantilla para asegurar que el archivo mantenga el formato correcto (Rut, Nombres, Apellidos, Correo). Cada fila se registrará como estudiante asociado a su organización, con usuario y contraseña.
							</p>
						</div>
						<div className="div-codig">



							{/* Código organización visible arriba */}
							{loadingInfo && (
								<div className="div-codig-emp" aria-busy="true">
									<span className="">Cargando información de la organización…</span>
								</div>
							)}
							{!loadingInfo && infoError && (
								<div className="alert alert-warning portal-emp-alert" role="alert">
									<i className="ri-error-warning-line me-2" aria-hidden />
									<span>{infoError}</span>
								</div>
							)}
							{!loadingInfo && !infoError && (
								<div className="div-codig-emp text-nowrap">
									<span>Código de su organización:</span>
									{companyInfo?.excel_code ? (
										<code className="codig-card text-nowrap"><span>{companyInfo.excel_code}</span></code>
									) : (
										<span className="portal-emp-code-sin-text">Sin asignar. Solicite al administrador que genere el &quot;Código para Excel&quot; en backoffice (ficha de la organización).</span>
									)}
								</div>
							)}
						</div>
					</div>

					{/* Paso 1: Plantilla */}
					<section className="card card-body-excel mb-3 bg-transparent">
						<div className="card-body">


							<div className="portal-emp-card-header">
								<span className="btn-excel">1</span>
								<h5 className="mb-0">Descargar plantilla Excel</h5>
							</div>
							<p>
								La plantilla incluye las columnas: <strong>Rut</strong>, <strong>Nombres</strong>, <strong>Apellidos</strong>, <strong>Correo</strong>. Complete una fila por estudiante. El RUT debe tener formato 12345678-9 (con guion). Al cargar el archivo, cada fila se dará de alta como estudiante de su organización con usuario y contraseña.
							</p>
							<button
								type="button"
								className="btn-excel-emp"
								onClick={handleDownloadTemplate}
								disabled={downloading}
							>
								{downloading ? "Descargando…" : (
									<>
										<i className="ri-file-excel-2-line me-2" aria-hidden />
										<span>Descargar plantilla obligatoria</span>
									</>
								)}
							</button>
						</div>
					</section>

					{/* Paso 2: Subir + Código obligatorio */}
					<section className="card card-body-updown mb-3">
						<div className="card-body">


							<div className="portal-emp-card-header">
								<span className="btn-up">2</span>
								<h5 className="mb-0">Completar y subir el archivo</h5>
							</div>
							{!templateDownloaded && (
								<div className="alert alert-warning py-2 small mb-2 mt-2">
									<i className="ri-error-warning-line me-1"></i>
									<strong>Importante:</strong> Debe descargar la plantilla obligatoria (Paso 1) antes de cargar el archivo. Es obligatorio utilizar el formato provisto.
								</div>
							)}
							<p className="portal-emp-card-text">
								Seleccione el archivo Excel e ingrese el <strong>Código para Excel</strong> de su organización. El sistema validará la coincidencia y creará los estudiantes asociados a su organización.
							</p>
							<form onSubmit={onSubmit} className="portal-emp-upload-form">
								<div className="portal-emp-step2-row">
									<div className="portal-emp-step2-file">
										<label htmlFor="file-cargas" className="form-label">Archivo Excel (.xlsx o .xls)</label>
										<input
											id="file-cargas"
											type="file"
											accept=".xlsx,.xls"
											className={`form-control ${fieldErrors.file ? "is-invalid" : ""}`}
											onChange={(e) => { setFile(e.target.files?.[0] || null); if (fieldErrors.file) setFieldErrors((p) => ({ ...p, file: undefined })); }}
											disabled={!templateDownloaded}
											aria-invalid={!!fieldErrors.file}
										/>
										{fieldErrors.file && <div className="invalid-feedback">{fieldErrors.file}</div>}
										{file && (
											<small className="text-muted d-block mt-1">
												Seleccionado: {file.name}
											</small>
										)}
									</div>
									<div className="portal-emp-step2-code">
										<label htmlFor="excel-code-input" className="form-label">
											Código para Excel <span className="text-danger">*</span>
										</label>
										<p className="portal-emp-code-desc small text-muted mb-1">
											Ingrese aquí el código asignado en backoffice (ficha de la organización, botón &quot;Obtener código&quot;). Debe coincidir con el código de su organización.
										</p>
										<input
											id="excel-code-input"
											type="text"
											className={`form-control portal-emp-code-input ${fieldErrors.excelCode ? "is-invalid" : ""}`}
											placeholder={companyInfo?.excel_code ? companyInfo.excel_code : "Ej: EMP2-ABC123"}
											value={excelCode}
											onChange={(e) => { setExcelCode(e.target.value); if (fieldErrors.excelCode) setFieldErrors((p) => ({ ...p, excelCode: undefined })); }}
											disabled={loadingInfo || !templateDownloaded}
											aria-invalid={!!fieldErrors.excelCode}
										/>
										{fieldErrors.excelCode && <div className="invalid-feedback">{fieldErrors.excelCode}</div>}
										{companyInfo?.excel_code && (
											<button
												type="button"
												className="btn-up-emp-trns mt-2"
												onClick={() => setExcelCode(companyInfo.excel_code)}
											>
												<span>Usar código de la organización</span>
											</button>
										)}
									</div>
								</div>
								<button className="btn-up-emp" type="submit" disabled={!canSubmit}>
									{loading ? "Procesando…" : (
										<>
											<i className="ri-upload-cloud-2-line me-1" aria-hidden />
											<span>Subir archivo</span>
										</>
									)}
								</button>
							</form>
						</div>
					</section>
					{error && (
						<div className="alert alert-danger portal-emp-alert" role="alert">
							<i className="ri-error-warning-line me-2" aria-hidden />
							{error}
						</div>
					)}

					{result && (
						<div className="alert alert-success portal-emp-alert portal-emp-result" role="alert">
							<h4 className="alert-heading">Carga completada</h4>
							<p className="mb-2">
								<strong>Lote ID:</strong> <code className="bg-white px-2 py-1 rounded">{result.batch_id}</code>
								<span className="text-muted ms-2">— Utilícelo en &quot;Matrícula masiva&quot;</span>
							</p>
							<hr />
							<ul className="mb-0">
								<li>Nuevos creados: <strong>{result.created}</strong></li>
								<li>Actualizados: <strong>{result.updated}</strong></li>
								{result.reassigned > 0 && <li>Reasignados a esta organización: <strong>{result.reassigned}</strong></li>}
								{result.skipped > 0 && <li>Omitidos (RUT duplicado o con curso activo en otra organización): <strong>{result.skipped}</strong></li>}
							</ul>
						</div>
					)}

					{/* Lotes cargados */}
					<section className="card card-table">
						<div className="card-body">


							<div className="portal-emp-card-header">
								<h5 className="mb-0">Lotes cargados</h5>
							</div>
							<p className="portal-emp-card-text">
								Cada archivo cargado genera un lote. Utilice ese lote en <strong>Matrícula masiva</strong> para inscribir estudiantes. Puede eliminar planillas antiguas si ya no aplican (los estudiantes no se eliminan).
							</p>
							{loadingBatches && <p className="text-muted mb-0">Cargando lotes…</p>}
							{successMsg && (
								<div className="alert alert-success portal-emp-alert" role="alert">
									<i className="ri-checkbox-circle-line me-2" aria-hidden></i>
									{successMsg}
								</div>
							)}
							{batchesError && (
								<div className="alert alert-warning portal-emp-alert mb-2" role="alert">
									{batchesError}
								</div>
							)}
							{!loadingBatches && !batchesError && batches.length === 0 && (
								<p className="text-muted mb-0">Aún no hay lotes registrados. Cargue el primer archivo Excel en el Paso 2.</p>
							)}
							{!loadingBatches && batches.length > 0 && (
								<div className="table-responsive table-wrapper-scroll">
									<table className="table portal-emp-table mb-0">
										<thead>
											<tr>
												<th>Lote</th>
												<th>Archivo</th>
												<th>Fecha</th>
												<th>Resumen</th>
												<th>Acciones</th>
											</tr>
										</thead>
										<tbody>
											{batches.map((b) => (
												<tr key={b.id}>
													<td><span>{b.id}</span></td>
													<td><span>{b.file_name}</span></td>
													<td><span>{formatBatchDate(b.created_at)}</span></td>
													<td>
														{b.created_count > 0 && <span>+{b.created_count}</span>}
														{b.updated_count > 0 && <span>~{b.updated_count}</span>}
														{b.reassigned_count > 0 && <span className=" ms-1 text-warning">↔{b.reassigned_count}</span>}
														{b.skipped_count > 0 && <span className=" ms-1 text-muted">−{b.skipped_count}</span>}
													</td>
													<td>
														<div className="portal-emp-batch-actions">
															<button
																type="button"
																className="adm-see-btn"
																title="Ver filas del Excel y resultado"
																onClick={() => openBatchPreview(b.id, b.file_name)}
															>
																<i className="ri-eye-fill" aria-hidden />
															</button>
															<button
																type="button"
																className="adm-del-btn"
																disabled={deletingId === b.id}
																onClick={() => setBatchToDelete(b)}
																title="Eliminar esta planilla"
															>
																<i className="ri-delete-bin-line"></i>
															</button>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</section>




				</div>

			</div>

			{previewBatchId != null &&
				createPortal(
					<div
						className="portal-emp-batch-overlay"
						role="presentation"
						onClick={closeBatchPreview}
					>
						<div
							className="portal-emp-batch-dialog"
							role="dialog"
							aria-modal="true"
							aria-labelledby="batch-preview-title"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="portal-emp-batch-dialog-header">
								<div>
									<h5 id="batch-preview-title">Contenido del lote</h5>
									<p className="text-muted small mb-0">
										<strong>
											Lote{" "}
											{previewBatchId > 0 ? previewBatchId : "—"}
										</strong>
										{previewFileName ? (
											<>
												{" "}
												· {capitalizeFirstLetter(previewFileName)}
											</>
										) : null}
									</p>
								</div>
								<button
									type="button"
									className="btn-close"
									aria-label="Cerrar"
									onClick={closeBatchPreview}
								/>
							</div>
							<div className="portal-emp-batch-legend">
								<span className="portal-emp-batch-legend-item">
									<span className="portal-emp-batch-legend-swatch created" aria-hidden />
									<span>
										<strong>Verde:</strong> usuario nuevo creado con esta carga
									</span>
								</span>
								<span className="portal-emp-batch-legend-item">
									<span className="portal-emp-batch-legend-swatch updated" aria-hidden />
									<span>
										<strong>Amarillo:</strong> ya era estudiante de su organización; datos
										actualizados desde el Excel
									</span>
								</span>
								<span className="portal-emp-batch-legend-item">
									<span className="portal-emp-batch-legend-swatch reassigned" aria-hidden />
									<span>
										<strong>Rojo:</strong> usuario que ya existía (p. ej. otra organización) y
										se reasignó a su organización
									</span>
								</span>
								<span className="portal-emp-batch-legend-item">
									<span className="portal-emp-batch-legend-swatch skipped" aria-hidden />
									<span>
										<strong>Gris:</strong> fila omitida (duplicado en archivo, RUT de
										organización, curso activo en otra organización, etc.)
									</span>
								</span>
							</div>
							<div className="portal-emp-batch-dialog-body">
								{previewLoading && (
									<p className="text-muted mb-0">Cargando filas…</p>
								)}
								{previewError && (
									<div className="alert alert-warning mb-0" role="alert">
										{previewError}
									</div>
								)}
								{!previewLoading && !previewError && previewRows.length === 0 && (
									<p className="text-muted mb-0">
										No hay detalle por fila para este lote (cargas anteriores a esta
										función no guardan fila a fila). Cargue un archivo nuevo para ver el
										desglose.
									</p>
								)}
								{!previewLoading && !previewError && previewRows.length > 0 && (
									<div className="table-responsive">
										<table className="table table-sm table-bordered align-middle mb-0 portal-emp-batch-preview-table">
											<thead className="table-light">
												<tr>
													<th>#</th>
													<th>RUT</th>
													<th>Nombres</th>
													<th>Apellidos</th>
													<th>Correo</th>
													<th>Resultado</th>
													<th>Observación</th>
												</tr>
											</thead>
											<tbody>
												{previewRows.map((r) => (
													<tr
														key={`${r.row_number}-${r.rut}`}
														className={rowOutcomeTableClass(r.outcome)}
													>
														<td>{r.row_number}</td>
														<td>{displayExcelCell(r.rut)}</td>
														<td>{displayExcelCell(r.nombres)}</td>
														<td>{displayExcelCell(r.apellidos)}</td>
														<td>{displayExcelCell(r.correo)}</td>
														<td>{rowOutcomeLabel(r.outcome)}</td>
														<td className="small text-break">
															{displayExcelCell(r.error_message)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
						</div>
					</div>,
					document.body,
				)}
			<ConfirmModal
				open={!!batchToDelete}
				onClose={() => setBatchToDelete(null)}
				onConfirm={confirmDeleteBatch}
				title="Eliminar lote"
				message={`¿Eliminar el lote "${batchToDelete?.file_name}"? Los estudiantes (usuarios) no se eliminarán, solo dejará de aparecer esta planilla en el historial.`}
				confirmLabel={deletingId === batchToDelete?.id ? "Eliminando..." : "Eliminar"}
				loading={deletingId === batchToDelete?.id}
			/>
		</>
	);
}
